import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  CaretDown,
  Check,
  CheckCircle,
  DeviceMobile,
  Truck,
  X,
} from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import useApi from '../../../utils/AxiosWrapper';
import { useUser } from '../../../utils/UserContext';
import { useScreenView } from '../../../utils/ScreenViewContext';
import useFrontChat from '../../../hooks/useFrontChat';
import { GreenGradientButton } from '../../../shared/Buttons';
import TrustSignals from '../TrustSignals';
import {
  AUTHORITY_NO,
  AUTHORITY_YES,
  CARRIER_LOOKUP_TIMEOUT_MS,
  CARRIER_NAME_LOOKUP_ROUTE,
  CARRIER_PATCH_RETRIES,
  CARRIER_PREFILL_HANDOFF_GRACE_MS,
  CARRIER_TYPE_DOT,
  CARRIER_TYPE_MC,
  formatCarrierCandidateLabel,
  formatCarrierIdentifierLabel,
  formatConfirmBody,
  HEADS_UP_COPY,
  HEADS_UP_META_TEXT_CLASS,
  HEADS_UP_PHASE_CONFIRM,
  HEADS_UP_PHASE_FORM,
  HEADS_UP_PHASE_INTRO,
  HEADS_UP_PHASE_LOOKING_UP,
  HEADS_UP_PHASE_LOOKUP_FAILED,
  LOADING_COMPLETION_GIVE_UP_MS,
  LOADING_COMPLETION_MS,
  LOADING_PROGRESS_HEAD_START,
  LOADING_STAGE_INTERVAL_MS,
  MC_DOT_MAX_LENGTH,
  MC_DOT_PATTERN,
  MC_NUMBER_MAX_LENGTH,
  VERIFICATION_STUCK_MAX_NUMBERS,
  VERIFICATION_STUCK_ROUTE,
  VERIFICATION_STUCK_THRESHOLD,
  verificationFailureStateKey,
  verificationStuckNotifiedKey,
} from '../../../constants/headsUp';
import { applyCarrierPrefill } from '../../../utils/carrierPrefill';
import {
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSessionTrySetItem,
} from '../../../shared/safeStorage';
import {
  buildPrefillAccountSnapshot,
  carrierPrefillIsCurrent,
  carrierPrefillKey,
  fetchCarrierPrefillOnce,
  markCarrierLookedUp,
} from '../../../utils/carrierPrefillRequest';

/**
 * Whether the progress bar should animate at all. A customer who has asked for reduced motion
 * gets a single static position instead, and a runtime with no animation frames (jsdom in some
 * suites) must not leave the lookup waiting on frames that will never come.
 */
const canAnimateProgress = () =>
  typeof window.requestAnimationFrame === 'function' &&
  !(
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

/**
 * The consecutive-failure run for one customer, as it is held in sessionStorage: how many
 * verification attempts in a row have come back a confirmed failure, and which numbers were
 * tried. Both travel together because the alert needs the numbers and the threshold needs the
 * count, and a customer who tries the same wrong number three times still has one number.
 */
type VerificationFailureState = { count: number; numbers: string[] };

const NO_VERIFICATION_FAILURES: VerificationFailureState = {
  count: 0,
  numbers: [],
};

/**
 * sessionStorage is unavailable outright in some privacy modes (Brave shields, Safari's "block
 * all cookies") and throws on write once a quota is hit. None of this page's behaviour is worth
 * an exception — but since THT-738 took away the continue path, silently forgetting the run is
 * no longer harmless either: a customer whose browser refuses storage would count from zero on
 * every failure, never reach the threshold, and sit on a blocking screen with nobody told.
 *
 * So the fallback is an in-memory mirror, which carries the run for the current page load; the
 * only thing lost without real storage is surviving a refresh. The mirror is consulted ONLY for
 * keys storage has actually refused, so a working browser reads and writes sessionStorage alone
 * and nothing stale can outlive a `sessionStorage.clear()`.
 */
type SessionMirror = Map<string, string>;

const rawReadSession = (key: string): string | null => safeSessionGetItem(key);

/**
 * Whether the value actually made it into real storage.
 *
 * The boolean is load-bearing: a false sends the caller to the in-memory
 * mirror below. It must come from the write itself — a wrapper that swallows
 * the failure and returns void would report success for every dropped write
 * and quietly disable the fallback.
 */
const rawWriteSession = (key: string, value: string): boolean =>
  safeSessionTrySetItem(key, value);

const rawClearSession = (key: string) => {
  // Nothing to do if it will not remove — such a storage never stored either.
  safeSessionRemoveItem(key);
};

/** The stored run, or a clean one when it is absent, unreadable or malformed. */
const parseFailureState = (raw: string | null): VerificationFailureState => {
  if (!raw) return NO_VERIFICATION_FAILURES;
  try {
    const parsed = JSON.parse(raw);
    return {
      count: typeof parsed?.count === 'number' ? parsed.count : 0,
      numbers: Array.isArray(parsed?.numbers) ? parsed.numbers : [],
    };
  } catch {
    return NO_VERIFICATION_FAILURES;
  }
};

interface HeadsUpProps {
  /**
   * Advance the signup flow to Business Info. Called after every terminal path.
   *
   * `carrierIdentitySaved` says whether the customer record actually changed, because the handoff
   * refetches it and only a save makes that worth awaiting. The save-failure skip link passes
   * false: nothing was written, and unlike `persistThenContinue` — which deliberately leaves its
   * spinner running across the handoff — that link has no pending state of its own, so an awaited
   * round-trip there is a click that visibly does nothing and invites more clicks.
   */
  onContinue: (carrierIdentitySaved: boolean) => void;

  /**
   * The customer came back through Business Info's "Not right?" link to change an MC/DOT we
   * already hold, rather than being sent here for the first time.
   *
   * It opens the page on the authority question instead of the intro. Someone correcting a
   * number has already read what the application will ask for and how long it takes — making
   * them click past it again to reach the one field they came for turns a correction into two
   * screens. The intro is still one Back away if they want the "why".
   */
  correctingSavedIdentity?: boolean;
}

/** A carrier the lookup resolved, and which identifier key resolved it. */
type CarrierCandidate = { identifierType?: string; legalName: string };

type LookupOutcome =
  | { found: true; candidates: CarrierCandidate[] }
  | { found: false; reason: 'not_found' | 'unavailable' };

/**
 * Post-login heads-up page (THT-729 PR 1; verification made required by THT-738).
 *
 * Collects the MC/DOT number (or, for leased-on drivers, whose authority they run under) right
 * after first login, mentions the mobile app, and runs a Highway lookup the customer must get
 * past: as of THT-738 there is no "continue, we'll verify later" on either failure state — a
 * registry-confirmed not-found asks for a corrected number, and a registry we could not reach
 * asks for a retry in a few minutes.
 *
 * That collection is spread over two screens rather than one: INTRO welcomes the customer with
 * "What happens next" and the mobile-app mention and asks for nothing, and its Continue reveals
 * FORM — the authority question and the one field. Neither screen calls the backend; the only
 * request either of them can start is the lookup, off FORM's Continue, exactly as before.
 * A customer who arrives through the "Not right?" correction opens on FORM — see
 * `correctingSavedIdentity`.
 *
 * The safety net that replaces the old escape hatch is a human one. Three CONFIRMED failures in
 * a row — a not-found, or "neither of these is my business" on the ambiguous screen; never an
 * outage — fire one fire-and-forget ping to customer service, who reach out. The run is counted
 * in sessionStorage per customer so a refresh does not reset it, and a flag there keeps the
 * ping to exactly one per session.
 *
 * Persistence is the existing `PATCH /customers/{id}` with exactly the field names BusinessInfo
 * sends today. This page NEVER touches `/applications/upsert` — application creation still
 * happens at Business Info, so `APPLICATION_STARTED` timing is unchanged.
 *
 * Phase 6 (THT-711) adds the prefill: once the customer CONFIRMS a carrier, one best-effort
 * call fetches what Highway knows about it and seeds the Business Info, Personal Info and
 * Assets steps through the sessionStorage shapes they already hydrate from. It is strictly a
 * convenience — it races the customer patch, every failure mode resolves to "no prefill", and
 * nothing on this page renders differently either way.
 */
function HeadsUp({ onContinue, correctingSavedIdentity }: HeadsUpProps) {
  useFrontChat();

  const { userId, userRecord } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  // The identity the customer authenticated with — the account-tier fallback for a customer row
  // that carries no email of its own. See `buildPrefillAccountSnapshot`.
  const { user } = useAuth0();

  const [phase, setPhase] = useState<string>(
    correctingSavedIdentity ? HEADS_UP_PHASE_FORM : HEADS_UP_PHASE_INTRO
  );
  // Pre-answered "Yes" — the overwhelming common case is a carrier with their own authority,
  // and the number field is already focused when the question screen opens. A leased-on driver
  // still switches in one click; everyone else saves two.
  const [authority, setAuthority] = useState<string | null>(AUTHORITY_YES);
  const [mcDotNumber, setMcDotNumber] = useState('');
  const [ownerName, setOwnerName] = useState('');
  /**
   * Which identifier the number IS, on the authority-No path only (THT-752).
   *
   * The Yes path never needs this — its lookup reports `identifier_type` and the customer
   * confirms a carrier by name. The No path runs no lookup, so nothing resolves the type but the
   * customer, and it cannot be inferred: MC and DOT ranges overlap, which is the whole reason the
   * confirm screen has an ambiguous-match state. Guessing by length only separates the 9-10 digit
   * tail; a 6- or 7-digit DOT — the common shape — would silently land in the MC column and
   * resolve a different carrier.
   *
   * Deliberately opens on NEITHER, unlike the authority question above it and unlike
   * BusinessInfo's select. A preselected MC is still a guess, just one wearing the customer's
   * name: leave it untouched and a DOT lands in the MC column exactly as the length rule put it
   * there. It is the likelier mistake here, not a remote one — the hint under the field sends the
   * driver to the side of the truck, which is where the USDOT is painted. Since a wrong answer
   * resolves a DIFFERENT carrier and that carrier's safety record feeds the prequal
   * (tach-architecture#1607), this is worth one tap to state rather than infer.
   */
  const [carrierType, setCarrierType] = useState<string | null>(null);
  const [stageIndex, setStageIndex] = useState(0);
  // One entry for a clean match; two (primary + alternate) when the number is ambiguous.
  const [candidates, setCandidates] = useState<CarrierCandidate[]>([]);
  // Null until the customer picks. A single match is pre-selected; an ambiguous one is not —
  // guessing for them is exactly the mistake the alternate exists to prevent.
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState<
    number | null
  >(null);
  // The number the lookup actually ran on. The retry field on the failure notice is editable,
  // so `trimmedNumber` drifts as the customer types — anything reporting a registry RESULT has
  // to keep naming what was really sent, not what is being typed now.
  const [lookedUpNumber, setLookedUpNumber] = useState('');
  const [saveFailed, setSaveFailed] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [failureReason, setFailureReason] = useState<
    'not_found' | 'unavailable'
  >('unavailable');
  const [saving, setSaving] = useState(false);
  // Whether customer service has already been told this customer is stuck. Seeded from
  // sessionStorage on mount so the line survives a refresh, and so does the no-re-fire rule.
  const [teamNotified, setTeamNotified] = useState(false);

  const [finishing, setFinishing] = useState(false);

  const failureStateKey = verificationFailureStateKey(userId ?? '');
  const stuckNotifiedKey = verificationStuckNotifiedKey(userId ?? '');

  /**
   * Fallback for the keys real storage refuses. It lives in a ref, so its lifetime is exactly
   * this page load — the same scope sessionStorage would have given us minus surviving a
   * refresh, and nothing that can outlive the page and greet a later visit.
   */
  const sessionMirror = useRef<SessionMirror>(new Map());

  const readSession = useCallback(
    (key: string) => sessionMirror.current.get(key) ?? rawReadSession(key),
    []
  );

  const writeSession = useCallback((key: string, value: string) => {
    if (rawWriteSession(key, value)) {
      sessionMirror.current.delete(key);
      return;
    }
    sessionMirror.current.set(key, value);
  }, []);

  const clearSession = useCallback((key: string) => {
    sessionMirror.current.delete(key);
    rawClearSession(key);
  }, []);

  useEffect(() => {
    setTeamNotified(readSession(stuckNotifiedKey) !== null);
  }, [stuckNotifiedKey, readSession]);

  const stageTimer = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined
  );
  const progressFill = useRef<HTMLDivElement | null>(null);
  // Raised the moment the lookup settles; the animation loop picks it up on its next frame.
  const lookupSettled = useRef(false);
  // Held while the stuck ping is out, so the "already told them" guard covers the request too
  // and not just the flag it writes when it comes back.
  const stuckSendInFlight = useRef(false);
  // Resolves `finishProgress()` once the bar has actually reached full.
  const completionResolve = useRef<(() => void) | null>(null);

  const stopStageTimer = useCallback(() => {
    if (stageTimer.current) {
      clearInterval(stageTimer.current);
      stageTimer.current = undefined;
    }
  }, []);

  useEffect(() => stopStageTimer, [stopStageTimer]);

  /**
   * Drives the lookup progress bar off the animation-frame clock and writes the result straight
   * to the DOM through a ref. Both choices are about smoothness: a bar driven by `stageIndex`
   * can only jump in thirds every LOADING_STAGE_INTERVAL_MS, and one driven by React state
   * would re-render this whole page on every frame to move a 6px bar.
   *
   * The fill travels at a constant velocity of exactly one segment per stage interval, so it
   * glides past each step label as that label lights up and there is no acceleration anywhere
   * to read as a stutter. Over the last segment the straight line hands off to `1 - e^-x`,
   * whose slope at the handover is identical to the line's — so the bar eases out with no
   * velocity break and only ever approaches full. A lookup that is still running must never
   * show a finished bar.
   *
   * The crawl is only ever an estimate, so the real request gets the last word: when the
   * lookup settles, the bar leaves the curve wherever it had got to and runs to full, and the
   * screen does not change until it lands there.
   *
   * Only `transform` is touched, so every frame stays on the compositor and never triggers
   * layout. The fill's own corners are square and the track clips them, because a rounded cap
   * would be visibly squashed by the horizontal scale.
   */
  useEffect(() => {
    if (phase !== HEADS_UP_PHASE_LOOKING_UP) return undefined;

    const fill = progressFill.current;
    if (!fill) return undefined;

    const segments = HEADS_UP_COPY.loadingStages.length;
    const rampMs = segments * LOADING_STAGE_INTERVAL_MS;
    // Where the linear run ends and the asymptotic tail takes over.
    const handover = (segments - 1) / segments;

    const paint = (travelled: number) => {
      const scale =
        LOADING_PROGRESS_HEAD_START +
        (1 - LOADING_PROGRESS_HEAD_START) * travelled;
      fill.style.transform = `scaleX(${scale})`;
    };

    // Reduced motion: one honest static position rather than a bar in flight. `finishProgress`
    // resolves on its own in this case, so the lookup is never left waiting on frames.
    if (!canAnimateProgress()) {
      paint(handover);
      return undefined;
    }

    let frame = 0;
    // Timebase comes from the first frame rather than performance.now(), so elapsed time is
    // always measured against the same clock that drives the frames.
    let startedAt: number | null = null;
    // Set on the first frame after the lookup settles, capturing where the crawl had reached.
    let settledAt: { time: number; from: number } | null = null;

    const step = (now: number) => {
      if (startedAt === null) startedAt = now;
      const linear = (now - startedAt) / rampMs;
      const crawl =
        linear < handover
          ? linear
          : handover +
            (1 - handover) *
              (1 - Math.exp(-(linear - handover) / (1 - handover)));

      if (lookupSettled.current && settledAt === null) {
        settledAt = { time: now, from: crawl };
      }

      if (settledAt === null) {
        paint(crawl);
      } else {
        const t = Math.min(1, (now - settledAt.time) / LOADING_COMPLETION_MS);
        // easeOutCubic. Unlike the crawl this one deliberately breaks velocity on the first
        // frame — the bar visibly picks up the moment the answer lands, which is the whole
        // signal, then glides into the stop rather than slamming into it.
        const eased = 1 - (1 - t) ** 3;
        paint(settledAt.from + (1 - settledAt.from) * eased);
        if (t >= 1) {
          const done = completionResolve.current;
          completionResolve.current = null;
          done?.();
          return; // Bar is full; nothing left to animate.
        }
      }

      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(frame);
      // Never strand the lookup on a promise this loop can no longer resolve.
      const pending = completionResolve.current;
      completionResolve.current = null;
      pending?.();
    };
  }, [phase]);

  /**
   * Tells the bar the request is done and resolves once it has actually reached full, so the
   * caller can hold the screen for exactly as long as the bar needs and no longer.
   */
  const finishProgress = () =>
    new Promise<void>((resolve) => {
      if (!progressFill.current || !canAnimateProgress()) {
        resolve();
        return;
      }

      // Frames stop in a backgrounded tab; timers do not. Whatever happens to the animation,
      // the flow moves on.
      const giveUp = setTimeout(() => {
        completionResolve.current = null;
        resolve();
      }, LOADING_COMPLETION_GIVE_UP_MS);

      completionResolve.current = () => {
        clearTimeout(giveUp);
        resolve();
      };
      lookupSettled.current = true;
    });

  const trimmedNumber = mcDotNumber.trim();
  const numberIsValid = MC_DOT_PATTERN.test(trimmedNumber);

  /**
   * Shared by BOTH number inputs — the one on the form and the retry one on the failure notice.
   * Keeping them on one handler is the point: when only the form capped its length, a customer
   * who typed an 11th digit into the retry field disabled every action on that screen with
   * nothing on the page saying why.
   */
  /**
   * The cap follows the named type on the No path, exactly as BusinessInfo's does
   * (`maxLength={carrierType === 'MC' ? 8 : 10}`). The Yes path keeps the union of the two: its
   * lookup is what resolves the type, so the field cannot know which cap applies while it is
   * being typed.
   */
  const numberMaxLength =
    authority === AUTHORITY_NO && carrierType === CARRIER_TYPE_MC
      ? MC_NUMBER_MAX_LENGTH
      : MC_DOT_MAX_LENGTH;
  const handleNumberChange = (value: string) =>
    setMcDotNumber(value.replace(/[^\d]/g, '').slice(0, numberMaxLength));
  /**
   * Switching to MC re-applies its shorter cap, so a 10-digit DOT already typed cannot survive
   * the switch as an over-length MC the backend is bound to reject.
   */
  const handleCarrierTypeChange = (value: string) => {
    setCarrierType(value);
    if (value === CARRIER_TYPE_MC) {
      setMcDotNumber((current) => current.slice(0, MC_NUMBER_MAX_LENGTH));
    }
  };
  /**
   * THT-752: the No path asks for the number as well as the owner name. A leased-on driver has
   * no authority of their own, but they still roll under somebody's — and letting them through
   * with both identifier columns null is what dead-ended them at Review, since the carrier-details
   * workflow has nothing to look up. Same digits-only rule as the Yes path, so one field validates
   * both.
   */
  const canContinue =
    authority === AUTHORITY_YES
      ? numberIsValid
      : authority === AUTHORITY_NO
        ? numberIsValid && carrierType !== null && ownerName.trim().length > 1
        : false;

  /** The number question, worded for whoever the authority belongs to. */
  const mcDotQuestion =
    authority === AUTHORITY_NO
      ? HEADS_UP_COPY.mcDotUnderAuthorityQuestion
      : HEADS_UP_COPY.mcDotQuestion;

  /**
   * The single integration point for the THT-728 lookup. Every failure mode — non-200, network
   * error, malformed body, or a lookup that outruns CARRIER_LOOKUP_TIMEOUT_MS — resolves to a
   * not-found outcome instead of throwing, so the caller has no way to block the flow.
   */
  const lookupCarrierName = async (
    identifier: string
  ): Promise<LookupOutcome> => {
    const timeout = new Promise<LookupOutcome>((resolve) => {
      setTimeout(
        () => resolve({ found: false, reason: 'unavailable' }),
        CARRIER_LOOKUP_TIMEOUT_MS
      );
    });

    const request = (async (): Promise<LookupOutcome> => {
      try {
        const response = await callProtectedApi(
          `${CARRIER_NAME_LOOKUP_ROUTE}/${identifier}`,
          'get',
          {},
          undefined,
          undefined,
          undefined,
          0,
          // Best-effort lookup: this page renders its own quiet notice, so suppress the
          // generic error toast.
          true
        );
        const data = response?.data?.data;
        // Read the body before the status: a miss is normally a 200 `{found:false}`, but the
        // wrapper also hands back the body of a not-found-shaped 404, and telling the customer
        // "the registry is down" when we actually know their number is wrong sends them away
        // with a typo saved.
        if (data?.found === false) {
          return { found: false, reason: 'not_found' };
        }
        if (response?.status !== HttpStatusCode.Ok) {
          return { found: false, reason: 'unavailable' };
        }
        if (data?.found && data?.legal_name) {
          const primary: CarrierCandidate = {
            identifierType: data.identifier_type,
            legalName: data.legal_name,
          };
          // `ambiguous` means the digits resolved as BOTH an MC and a DOT number, for
          // different companies. Carry the alternate through so the customer can pick.
          const alternate: CarrierCandidate | undefined =
            data.ambiguous && data.alternate?.legal_name
              ? {
                  identifierType: data.alternate.identifier_type,
                  legalName: data.alternate.legal_name,
                }
              : undefined;
          return {
            found: true,
            candidates: alternate ? [primary, alternate] : [primary],
          };
        }
        return { found: false, reason: 'not_found' };
      } catch (e: any) {
        console.error('Carrier name lookup failed: ', e?.message);
        return { found: false, reason: 'unavailable' };
      }
    })();

    return Promise.race([request, timeout]);
  };

  /**
   * Which carrier the prefill has already been fetched for, so the call goes out exactly once
   * per confirmed carrier. A customer who retries a failed save does not re-fetch; one who goes
   * back, corrects their number and confirms a DIFFERENT carrier does — the guard is the
   * carrier, not the click.
   */
  const prefillRequestedFor = useRef<string | null>(null);

  const requestCarrierPrefill = async (
    identifier: string,
    identifierType: string
  ): Promise<void> => {
    const key = carrierPrefillKey(identifier, identifierType);
    if (prefillRequestedFor.current === key) return;
    prefillRequestedFor.current = key;

    const prefill = await fetchCarrierPrefillOnce(
      callProtectedApi,
      identifier,
      identifierType
    );
    /**
     * The customer has confirmed a DIFFERENT carrier while this one was still in flight, which is
     * an ordinary sequence: carrier A's save fails, they correct the number, and confirm B. Both
     * requests stay open, and whichever answers last would otherwise win. Applying A here would
     * put the company they just rejected back into the form — over B's name, address and truck
     * counts, and over the suggestion record that says which of them came from a carrier at all.
     * The guard on the way in cannot cover this; only re-reading after the await can.
     *
     * Asked of the module and NOT of `prefillRequestedFor`, because the correction the customer
     * takes from Business Info REMOUNTS this page: a ref is per-mount, so mount 1's copy would
     * still name carrier A and wave its own late response through over the B that mount 2 just
     * confirmed. See `carrierPrefillIsCurrent`.
     */
    if (!carrierPrefillIsCurrent(key)) return;
    if (!prefill) {
      // Nothing landed, so nothing was fetched — releasing the guard lets a customer who retries
      // a failed save get a second attempt at the prefill too. Holding it would turn one timed-out
      // request into a permanently un-prefilled application for the rest of the session.
      prefillRequestedFor.current = null;
      return;
    }
    // The same key identifies the carrier downstream, so a customer who goes back and confirms
    // a DIFFERENT carrier has the first one's untouched suggestions replaced rather than
    // protected as if they were data they had given us.
    applyCarrierPrefill(
      prefill,
      buildPrefillAccountSnapshot(userRecord, {
        email: user?.email,
        phoneNumber: user?.phone_number,
      }),
      key
    );
    // Business Info re-fetches when this does not match the carrier on the record; marking it
    // here keeps that from firing a second, pointless lookup for a carrier we just handled.
    markCarrierLookedUp(key);
  };

  /**
   * Tells customer service this trucker is stuck (THT-738). Deliberately fire-and-forget: the
   * customer is already on a screen that failed them, and a Slack ping that errors must never
   * become a second thing going wrong in front of them. Nothing on the page waits for it and
   * nothing on the page reads its result.
   *
   * The "we told them" flag is written only once the request has actually SUCCEEDED, and both
   * the flag and the line the customer reads come from that one moment. A ping that failed
   * leaves no flag, so the next confirmed failure gets to try again — the alternative, flagging
   * up front, means one bad request permanently tells the customer a human is coming while
   * customer service was never told, on a screen they can no longer leave.
   *
   * An in-flight ref covers the window between the request going out and the flag landing, so
   * two failures in quick succession still cannot double-send.
   */
  const notifyVerificationStuck = async (attemptedNumbers: string[]) => {
    if (readSession(stuckNotifiedKey) !== null || stuckSendInFlight.current) {
      return;
    }
    stuckSendInFlight.current = true;

    try {
      const response = await callProtectedApi(
        VERIFICATION_STUCK_ROUTE,
        'post',
        { attempted_numbers: attemptedNumbers },
        undefined,
        undefined,
        undefined,
        0,
        // No toast: the customer never asked for this call and must not be told it failed.
        true
      );
      // The wrapper hands back non-2xx responses rather than throwing on all of them, so the
      // status is what says the alert actually landed.
      const sent =
        response?.status === HttpStatusCode.Ok ||
        response?.status === HttpStatusCode.Created;
      if (!sent) {
        console.error(
          'Verification-stuck notification failed: ',
          response?.status
        );
        return;
      }
      writeSession(stuckNotifiedKey, String(true));
      setTeamNotified(true);
    } catch (e: any) {
      // Swallowed on purpose — the customer is already on a screen that failed them and must
      // not be shown a second thing going wrong.
      console.error('Verification-stuck notification failed: ', e?.message);
    } finally {
      stuckSendInFlight.current = false;
    }
  };

  /**
   * Counts one CONFIRMED verification failure — a registry-answered not-found, or the customer
   * telling us neither ambiguous candidate is theirs. Outages and timeouts are deliberately not
   * routed here: the registry never answered, so they say nothing about whether this trucker is
   * stuck, and Sentry already owns our own downtime.
   *
   * On the threshold-th consecutive failure the alert goes out with the numbers tried.
   */
  const recordVerificationFailure = (attemptedNumber: string) => {
    const stored = parseFailureState(readSession(failureStateKey));
    const count = stored.count + 1;
    // Same number twice is one number to a human reading the alert; keep the most recent few.
    const numbers = [
      ...stored.numbers.filter((number) => number !== attemptedNumber),
      attemptedNumber,
    ]
      .filter(Boolean)
      .slice(-VERIFICATION_STUCK_MAX_NUMBERS);

    writeSession(failureStateKey, JSON.stringify({ count, numbers }));

    if (count >= VERIFICATION_STUCK_THRESHOLD) notifyVerificationStuck(numbers);
  };

  /** A confirmed carrier ends the run — the next failure, if any, starts counting from one. */
  const resetVerificationFailures = () => clearSession(failureStateKey);

  /**
   * Persists through the existing customer patch using exactly the fields BusinessInfo sends
   * today. Returns whether the write actually landed — the caller must not advance on a
   * failure, or the answer is lost silently and the gate simply re-asks next session.
   *
   * Deliberately does NOT call `setUserRecord`: a new userRecord identity re-triggers
   * signup.tsx's recent-application effect, which would drop a full-screen loading state on
   * the customer and race its own `setStep` against the gate's.
   */
  const persistCarrierIdentity = async (
    updates: Record<string, unknown>
  ): Promise<boolean> => {
    setSaving(true);
    setSaveFailed(false);
    try {
      const response = await callProtectedApi(
        `/customers/${userId ?? ''}`,
        'patch',
        updates,
        'Error occurred while saving your carrier details.',
        undefined,
        undefined,
        CARRIER_PATCH_RETRIES
      );
      const data = response?.data?.data?.customer;
      const saved = Boolean(
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      );
      // Deliberately only cleared when the customer is STAYING on this page. A successful save
      // is followed by the handoff to Business Info, which takes a moment of its own; dropping
      // the button back to its resting state in between reads as "nothing happened" and invites
      // a second click on a save that already worked. The spinner runs until this unmounts.
      if (!saved) setSaving(false);
      return saved;
    } catch (e: any) {
      console.error(
        'Error occurred while saving carrier details: ',
        e?.message
      );
      setSaving(false);
      return false;
    }
  };

  /**
   * Save, then hand off to Business Info. On a failed save, stay put and say so.
   *
   * `prefilling` is the THT-711 prefill, already in flight since before the patch. It is waited
   * on so Business Info mounts with its session data already written — but only for
   * CARRIER_PREFILL_HANDOFF_GRACE_MS past the save, because a convenience feature must never be
   * what a customer who has finished this page is stuck watching a spinner for.
   *
   * The failure branch is settled BEFORE that wait. `persistCarrierIdentity` has already given
   * the button back by the time it returns false, so waiting first would leave the customer
   * looking at a live Continue button and no error for up to the whole grace — long enough to
   * click again and fire a second patch at a save they have not been told failed.
   */
  const persistThenContinue = async (
    updates: Record<string, unknown>,
    prefilling?: Promise<void>
  ) => {
    const saved = await persistCarrierIdentity(updates);
    if (!saved) {
      setSaveFailed(true);
      return;
    }
    if (prefilling) {
      await Promise.race([
        prefilling,
        new Promise<void>((resolve) => {
          setTimeout(resolve, CARRIER_PREFILL_HANDOFF_GRACE_MS);
        }),
      ]);
    }
    onContinue(true);
  };

  /**
   * The lookup reports which key resolved the number (`identifier_type`, 'MC' or 'DOT'), so a
   * customer who typed their DOT number into this single "MC or DOT" field still lands in the
   * right column — and when the number was ambiguous, the candidate they picked decides it.
   * With no lookup result to go on — the quiet-failure path — MC is the default, and the
   * backend re-checks the number later.
   */
  const continueWithNumber = () => {
    // Reachable only from a confirm screen now that neither failure state offers a way past
    // it — so getting here means the registry named a carrier and the customer said yes.
    resetVerificationFailures();
    const chosen =
      selectedCandidateIndex === null
        ? undefined
        : candidates[selectedCandidateIndex];
    const isDot =
      (chosen?.identifierType ?? '').toUpperCase() ===
      CARRIER_TYPE_DOT.toUpperCase();
    // THT-711: the prefill goes out alongside the patch, on the number and type the customer
    // has just confirmed. Started before the await so the customer waits for the slower of the
    // two rather than the sum of them.
    // Every failure inside is already swallowed, and the `catch` keeps it that way from the
    // outside too: on a failed save this promise is never awaited at all, and an unhandled
    // rejection from a convenience feature must not be what stops the customer's handoff.
    const prefilling = requestCarrierPrefill(
      trimmedNumber,
      isDot ? CARRIER_TYPE_DOT : CARRIER_TYPE_MC
    ).catch(() => undefined);
    return persistThenContinue(
      {
        motor_carrier_number: isDot ? undefined : trimmedNumber,
        dot_number: isDot ? trimmedNumber : undefined,
        owns_their_mc_dot_number: true,
      },
      prefilling
    );
  };

  /**
   * The leased-on path (THT-752). It saves the number the customer runs under next to the answer
   * that it is not theirs, so the record always carries an identifier for Highway to check.
   *
   * The number goes in UNVERIFIED — no lookup, no confirm screen. That is deliberate: the Yes
   * path's lookup asks "is this carrier YOU?", a question a lessee cannot answer about their
   * carrier's registry entry, and a not-found there BLOCKS. The backend re-checks the identifier
   * on the Review transition either way, and now bounces a bad one back to Business Info with a
   * warning instead of dead-ending.
   *
   * The column is the one the customer NAMED, not one inferred from the digits. Length cannot
   * tell the two apart below 9 digits — MC and DOT ranges overlap, which is exactly why the Yes
   * path has an ambiguous-match screen — so a 6- or 7-digit DOT, the common shape, would have
   * landed in the MC column and resolved a different carrier. The toggle costs one tap and
   * removes the guess; BusinessInfo asks the same question of everyone who reaches it.
   *
   * No prefill goes out: Highway would describe the carrier they are leased to, and this form is
   * about to ask for THEIR business.
   *
   * OPEN — UNDERWRITING, not a form question (John, tach-architecture#1607). The number saved
   * here belongs to the lessor, and `savePrequalDecisionData`
   * (`temporal/src/activities/highwayActivities/highwayActivities.ts`) builds the prequal almost
   * entirely from the `carrierDetails` Highway returns for whatever identifier we send —
   * `authority_age_requirement_passed`, `has_no_published_identity_alerts`,
   * `has_verified_manually_entered_identifier` — so this cohort is scored on someone else's
   * safety record and authority age, and that feeds the authority-age fee tiers. It may well be
   * the right answer for a leased owner-operator, whose operating authority genuinely IS the
   * lessor's, but it is a call for underwriting to make explicitly.
   *
   * This page does not foreclose it either way: `owns_their_mc_dot_number: false` rides in the
   * SAME patch as the number, so every consumer can tell a lessor's identifier from an
   * owner-operator's own. Nothing on the backend reads that flag today — grep it and you find
   * the migration, the model and the customers API, nothing in the prequal path.
   */
  const continueWithoutOwnAuthority = () => {
    const isDot = carrierType === CARRIER_TYPE_DOT;
    return persistThenContinue({
      motor_carrier_number: isDot ? undefined : trimmedNumber,
      dot_number: isDot ? trimmedNumber : undefined,
      owns_their_mc_dot_number: false,
      other_mc_dot_number_owner_name: ownerName.trim(),
    });
  };

  const runLookup = async () => {
    // Pinned for the whole run: everything downstream that reports what the registry said must
    // quote this, not the live field the failure screen hands back to the customer.
    const identifier = trimmedNumber;
    setLookedUpNumber(identifier);
    setPhase(HEADS_UP_PHASE_LOOKING_UP);
    setStageIndex(0);
    setFinishing(false);
    lookupSettled.current = false;
    stopStageTimer();
    stageTimer.current = setInterval(() => {
      setStageIndex((current) =>
        Math.min(current + 1, HEADS_UP_COPY.loadingStages.length - 1)
      );
    }, LOADING_STAGE_INTERVAL_MS);

    const outcome = await lookupCarrierName(identifier);
    stopStageTimer();

    // The request is what the bar was tracking, so it finishes on the request — whichever way
    // the lookup went. The steps all read as done because the checking really is done; what
    // was found is the next screen's business.
    setFinishing(true);
    await finishProgress();

    if (outcome.found) {
      setCandidates(outcome.candidates);
      // Pre-select the only match; make the customer choose when there are two.
      setSelectedCandidateIndex(outcome.candidates.length === 1 ? 0 : null);
      setPhase(HEADS_UP_PHASE_CONFIRM);
      return;
    }
    setCandidates([]);
    setSelectedCandidateIndex(null);
    setFailureReason(outcome.reason);
    // NOT FOUND clears the field: the registry has already rejected these digits, so re-sending
    // them is the one thing that cannot help, and a prefilled box invites exactly that. The
    // subheading still quotes what was tried, from `lookedUpNumber`, so nothing is lost.
    //
    // REGISTRY DOWN deliberately keeps the number. Its Continue path persists whatever is in
    // this field, and clearing it would force a customer to retype a perfectly good number
    // before they could carry on — which would make an outage of ours blocking through the
    // back door.
    if (outcome.reason === 'not_found') {
      setMcDotNumber('');
      // The registry ANSWERED — that is what makes this a failure worth counting. The
      // unavailable branch falls past this on purpose.
      recordVerificationFailure(identifier);
    }
    setPhase(HEADS_UP_PHASE_LOOKUP_FAILED);
  };

  const handleSubmit = () => {
    if (!canContinue || saving) return;
    if (authority === AUTHORITY_NO) {
      // Leased-on drivers have no MC/DOT of their own — skip the lookup entirely.
      continueWithoutOwnAuthority();
      return;
    }
    runLookup();
  };

  /**
   * Selected/unselected skin shared by every pick-one control on this page — the same
   * treatment BusinessInfo gives its selectable cards: 4px corners, `bordergray`, `shadow-sm`,
   * and `border-accent ring-1 ring-accent` once chosen. Selection is accent, not green,
   * because green belongs to the primary CTA everywhere else in this flow.
   */
  const choiceSkin = (selected: boolean) =>
    `rounded-[4px] border px-4 py-3 text-[15px] shadow-sm transition-all ${
      selected
        ? 'border-accent text-primary ring-1 ring-accent'
        : 'border-bordergray bg-white text-gray-700 hover:border-accent'
    }`;

  /** The label styling every field on AccountInfo / BusinessInfo uses. */
  const FIELD_LABEL = 'block text-sm font-medium text-gray-700';

  /** …and their input styling, down to the square corners and focus ring. */
  const FIELD_INPUT =
    'mt-1 block w-full border border-gray-300 px-3 py-2 focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm';

  /**
   * Says why the actions are disabled instead of leaving the customer guessing. `handleNumberChange`
   * already makes an invalid value unreachable by typing or pasting, so this is the backstop for a
   * value that arrives some other way (autofill, a restored session) — on a screen whose every
   * control is gated on the number, silence is the one thing it must never do.
   */
  const renderNumberInvalidHint = () =>
    trimmedNumber.length > 0 && !numberIsValid ? (
      <div className="mt-2 text-[13px] text-[#b04a18]">
        {HEADS_UP_COPY.mcDotInvalid}
      </div>
    ) : null;

  /**
   * The heading block the other signup steps open with: centred on mobile, left-aligned from
   * `lg`, title over a muted one-liner.
   */
  const renderHeader = (title: string, subtitle?: React.ReactNode) => (
    <div className="mx-auto mb-6 max-w-[600px] text-center lg:mb-8 lg:max-w-full lg:text-start">
      <h2 className="text-[30px] font-bold">{title}</h2>
      {subtitle ? <p className="text-gray-500">{subtitle}</p> : null}
    </div>
  );

  /** The body column those steps put their form in. */
  const renderBody = (children: React.ReactNode) => (
    <div className="mx-auto max-w-[600px] space-y-6 lg:max-w-full">
      {children}
    </div>
  );

  /**
   * The primary action, matching what SignupFooter renders for the customer steps — the shared
   * GreenGradientButton with a nudging arrow, centred under the form. SignupFooter itself is
   * keyed on ApplicationStep and this page deliberately is not one, so it borrows the button
   * rather than passing a step it does not have.
   */
  const renderPrimaryCta = (
    label: string,
    onClick: () => void,
    disabled: boolean,
    loading = false
  ) => (
    <div className="flex flex-col items-center justify-center gap-y-3">
      <GreenGradientButton disabled={disabled} onClick={onClick}>
        {loading ? (
          // Same spinner SignupFooter shows while a step is submitting.
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            <span>{HEADS_UP_COPY.processingLabel}</span>
          </div>
        ) : (
          <>
            <span>{label}</span>
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-1"
            />
          </>
        )}
      </GreenGradientButton>
    </div>
  );

  /**
   * The "a human is coming" reassurance, rendered on every screen the customer can be left on
   * once the alert has actually gone out — see notifyVerificationStuck for when that is.
   *
   * It has to be on the FORM as well as the failure screens: the threshold can be crossed from
   * the ambiguous screen's "Neither", which lands the customer back on the form, and a page
   * that pinged customer service without telling them is the whole thing this line exists to
   * avoid.
   */
  const renderTeamNotified = () =>
    teamNotified ? (
      <div
        className={`mt-4 text-center text-[14px] ${HEADS_UP_META_TEXT_CLASS}`}
      >
        {HEADS_UP_COPY.teamNotified}
      </div>
    ) : null;

  /** The secondary action — a plain underlined link under the CTA. */
  const renderSecondaryLink = (
    label: string,
    onClick: () => void,
    disabled = false
  ) => (
    <div className="mt-4 text-center">
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="text-[14px] text-gray-400 underline disabled:opacity-50"
      >
        {label}
      </button>
    </div>
  );

  const renderChoiceButton = (
    label: string,
    selected: boolean,
    onSelect: () => void,
    icon?: React.ReactNode
  ) => (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex flex-1 items-center justify-center gap-2 ${choiceSkin(
        selected
      )}`}
    >
      {icon}
      {label}
    </button>
  );

  const renderChoice = (label: string, value: string, icon: React.ReactNode) =>
    renderChoiceButton(
      label,
      authority === value,
      () => setAuthority(value),
      icon
    );

  /**
   * First half of the form: what the application is going to ask for, how long it takes, why we
   * need it, and the app. Nothing to fill in and nothing to get wrong — its Continue is always
   * live and only moves the page on.
   */
  const renderIntro = () => (
    <>
      {renderHeader(HEADS_UP_COPY.title, HEADS_UP_COPY.subtitle)}

      {renderBody(
        <>
          <div className="rounded-[4px] border border-bordergray bg-[#fafbfc] px-6 py-5 shadow-sm">
            <div className="mb-3.5 text-[13px] font-bold uppercase tracking-wide text-gray-500">
              {HEADS_UP_COPY.whatHappensNextTitle}
            </div>
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {HEADS_UP_COPY.whatHappensNextSteps.map((step, index) => (
                <div key={step.title} className="flex items-start gap-2.5">
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-none bg-[#fdeecd] text-[12px] font-bold text-[#b07818]">
                    {index + 1}
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-primary">
                      {step.title}
                    </div>
                    <div className="mt-0.5 text-[12px] text-gray-400">
                      {step.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-bordergray pt-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                {/* The two reassurances and the time estimate are one dot-separated line, so
                they wrap together and stay one thought rather than three loose chips. */}
                <div
                  data-testid="heads-up-meta"
                  className="flex flex-wrap items-center gap-x-2 gap-y-1.5"
                >
                  {[
                    ...HEADS_UP_COPY.reassurances,
                    HEADS_UP_COPY.timeEstimate,
                  ].map((item, index) => (
                    <Fragment key={item}>
                      {index > 0 && (
                        <span
                          aria-hidden="true"
                          className={`text-[13px] ${HEADS_UP_META_TEXT_CLASS}`}
                        >
                          &bull;
                        </span>
                      )}
                      <span
                        className={`text-[13px] ${HEADS_UP_META_TEXT_CLASS}`}
                      >
                        {item}
                      </span>
                    </Fragment>
                  ))}
                </div>
                {/* Collapsed by default — the answer is reassurance, not something the customer
                has to read to get through the page. Same colour as the time estimate it sits
                beside, from the one shared token. */}
                <button
                  type="button"
                  aria-expanded={whyOpen}
                  onClick={() => setWhyOpen((open) => !open)}
                  className={`inline-flex items-center gap-1 text-[13px] underline ${HEADS_UP_META_TEXT_CLASS}`}
                >
                  {HEADS_UP_COPY.whyTitle}
                  <CaretDown
                    size={12}
                    weight="bold"
                    className={whyOpen ? 'rotate-180' : undefined}
                  />
                </button>
              </div>
              {whyOpen && (
                /* Verbatim from the landing page's expandable — see HEADS_UP_COPY.whyBody. */
                <div className="mt-2 space-y-2 text-[13px] leading-relaxed text-gray-500">
                  <p>{HEADS_UP_COPY.whyBody}</p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    {HEADS_UP_COPY.whyBullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2.5 rounded-[4px] border border-bordergray bg-[#f8faf8] px-4 py-3 shadow-sm">
            <DeviceMobile size={18} color="#5b9a5e" />
            <span className="text-[13px] text-gray-500">
              {HEADS_UP_COPY.mobileAppPrefix}
              <span className="underline">{HEADS_UP_COPY.mobileAppName}</span>
              {HEADS_UP_COPY.mobileAppSuffix}
            </span>
          </div>

          {renderPrimaryCta(
            HEADS_UP_COPY.continueLabel,
            () => setPhase(HEADS_UP_PHASE_FORM),
            false
          )}
        </>
      )}
    </>
  );

  /**
   * Second half: the authority question and the one field. This is where every path back from a
   * later screen lands, so the "we've told the team" line lives here rather than on the intro.
   */
  const renderForm = () => (
    <>
      {renderHeader(
        HEADS_UP_COPY.authorityStepTitle,
        HEADS_UP_COPY.authorityStepSubtitle
      )}

      {renderBody(
        <>
          <div className="">
            <div className={FIELD_LABEL}>{HEADS_UP_COPY.authorityQuestion}</div>
            <div className="mt-3 flex gap-3">
              {renderChoice(
                HEADS_UP_COPY.authorityYesLabel,
                AUTHORITY_YES,
                <Check size={14} weight="bold" />
              )}
              {renderChoice(
                HEADS_UP_COPY.authorityNoLabel,
                AUTHORITY_NO,
                <X size={12} weight="bold" />
              )}
            </div>
          </div>

          {/* Asked on BOTH paths since THT-752 — see `continueWithoutOwnAuthority`. One input
              rather than one per branch, so a number already typed survives a customer changing
              their mind about the question above it. */}
          <div className="">
            <label htmlFor="heads-up-mc-dot" className={FIELD_LABEL}>
              {mcDotQuestion}
            </label>
            <input
              id="heads-up-mc-dot"
              value={mcDotNumber}
              // The one thing this page needs typed, and the authority question above it comes
              // pre-answered — so this screen is type-and-continue, with no click in between.
              // It focuses on arrival now rather than on mount of the whole page, which is
              // exactly when the customer is first asked for it.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              inputMode="numeric"
              maxLength={numberMaxLength}
              placeholder={HEADS_UP_COPY.mcDotPlaceholder}
              onChange={(e) => handleNumberChange(e.target.value)}
              className={FIELD_INPUT}
            />
            {authority === AUTHORITY_NO && (
              <div className="mt-2 text-[13px] text-gray-500">
                {HEADS_UP_COPY.mcDotUnderAuthorityHint}
              </div>
            )}
            {renderNumberInvalidHint()}
          </div>

          {/* Only the No path asks: the Yes path's lookup reports the type itself (THT-752). */}
          {authority === AUTHORITY_NO && (
            <div className="">
              <div className={FIELD_LABEL}>
                {HEADS_UP_COPY.carrierTypeQuestion}
              </div>
              <div className="mt-3 flex gap-3">
                {renderChoiceButton(
                  CARRIER_TYPE_MC,
                  carrierType === CARRIER_TYPE_MC,
                  () => handleCarrierTypeChange(CARRIER_TYPE_MC)
                )}
                {renderChoiceButton(
                  CARRIER_TYPE_DOT,
                  carrierType === CARRIER_TYPE_DOT,
                  () => handleCarrierTypeChange(CARRIER_TYPE_DOT)
                )}
              </div>
            </div>
          )}

          {authority === AUTHORITY_NO && (
            <div className="">
              <label htmlFor="heads-up-owner" className={FIELD_LABEL}>
                {HEADS_UP_COPY.ownerQuestion}
              </label>
              <input
                id="heads-up-owner"
                value={ownerName}
                placeholder={HEADS_UP_COPY.ownerPlaceholder}
                onChange={(e) => setOwnerName(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>
          )}

          {renderPrimaryCta(
            HEADS_UP_COPY.continueLabel,
            handleSubmit,
            !canContinue || saving,
            saving
          )}

          {/* Back to the intro, which is where "Why does TACH need this info?" is answered — and
              disabled mid-save for the same reason the CTA is: a save is already in flight. */}
          {renderSecondaryLink(
            HEADS_UP_COPY.backLabel,
            () => setPhase(HEADS_UP_PHASE_INTRO),
            saving
          )}

          {renderTeamNotified()}
        </>
      )}
    </>
  );

  const renderLookingUp = () => (
    <div className="pt-4 text-center">
      <div className="mx-auto mb-6 flex h-[68px] w-[68px] items-center justify-center rounded-full bg-[#eaf3ea]">
        <Truck size={30} color="#5b9a5e" weight="fill" />
      </div>

      {/* The staged copy is the live region; the bar underneath is decorative to AT. */}
      <div aria-live="polite">
        {/* Keyed so each new message re-runs the entrance instead of swapping in hard. */}
        <h1
          key={stageIndex}
          className="mb-2 animate-stage-in text-[24px] font-extrabold text-primary"
        >
          {HEADS_UP_COPY.loadingStages[stageIndex]}
        </h1>
      </div>
      <p className="mb-7 text-[15px] text-gray-500">
        {HEADS_UP_COPY.loadingSubtitle}
      </p>

      <div className="mx-auto max-w-[420px]">
        <div
          role="progressbar"
          aria-label={HEADS_UP_COPY.loadingProgressLabel}
          className="h-1.5 w-full overflow-hidden rounded-full bg-[#e8eaec]"
        >
          {/* The transform below is the pre-animation value only — it stops a full-width bar
              from flashing on the frame before the effect starts, and must stay CONSTANT.
              React skips unchanged style properties, which is what lets the animation frames
              own this transform; making it depend on state would fight them every re-render. */}
          <div
            ref={progressFill}
            data-testid="lookup-progress-fill"
            style={{
              transform: `scaleX(${LOADING_PROGRESS_HEAD_START})`,
              willChange: 'transform',
            }}
            className="h-full w-full origin-left bg-gradient-to-r from-[#5b9a5e] to-[#74b477]"
          />
        </div>

        <div className="mt-2.5 flex justify-between text-[12px]">
          {HEADS_UP_COPY.loadingStepLabels.map((label, index) => (
            <span
              key={label}
              className={`font-semibold transition-colors duration-500 ease-out ${
                finishing || stageIndex >= index
                  ? 'text-[#4c8a50]'
                  : 'text-gray-400'
              }`}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  /** Ambiguous match: the customer picks which of the two carriers is theirs. */
  const renderAmbiguousConfirm = () => (
    <>
      {renderHeader(HEADS_UP_COPY.ambiguousTitle)}
      <div className="mb-6 flex flex-col gap-3">
        {candidates.map((candidate, index) => {
          const selected = selectedCandidateIndex === index;
          const identifierType = candidate.identifierType ?? CARRIER_TYPE_MC;
          return (
            <button
              key={formatCarrierCandidateLabel(
                identifierType,
                lookedUpNumber,
                candidate.legalName
              )}
              type="button"
              aria-pressed={selected}
              // The card shows the identifier and name on two lines; AT gets them joined.
              aria-label={formatCarrierCandidateLabel(
                identifierType,
                lookedUpNumber,
                candidate.legalName
              )}
              onClick={() => setSelectedCandidateIndex(index)}
              className={`w-full rounded-[4px] border px-5 py-[18px] text-left shadow-sm transition-all ${
                selected
                  ? 'border-accent ring-1 ring-accent'
                  : 'border-bordergray bg-white hover:border-accent'
              }`}
            >
              <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wider text-gray-500">
                {formatCarrierIdentifierLabel(identifierType, lookedUpNumber)}
              </div>
              <div className="text-[18px] font-extrabold text-primary">
                {candidate.legalName}
              </div>
            </button>
          );
        })}
      </div>

      {renderPrimaryCta(
        HEADS_UP_COPY.continueLabel,
        continueWithNumber,
        selectedCandidateIndex === null || saving,
        saving
      )}
      {/* "Neither" is a confirmed verification failure, not a navigation event: the registry
          answered, and the customer has told us its answer is not them. It counts (John,
          2026-08-06). Where the link goes is unchanged — but now that it has a counting side
          effect it has to be disabled alongside the CTA while a confirm save is in flight,
          or an impatient second click banks a failure against a customer who just verified. */}
      {renderSecondaryLink(
        HEADS_UP_COPY.ambiguousNoneLabel,
        () => {
          recordVerificationFailure(lookedUpNumber);
          setPhase(HEADS_UP_PHASE_FORM);
        },
        saving
      )}
    </>
  );

  /** Single match: confirm the one carrier the registry returned. */
  const renderSingleConfirm = () => {
    const match = candidates[0];
    const matchedLabel = formatCarrierIdentifierLabel(
      match?.identifierType ?? CARRIER_TYPE_MC,
      lookedUpNumber
    );
    return (
      <>
        {renderHeader(
          HEADS_UP_COPY.confirmTitle,
          formatConfirmBody(matchedLabel)
        )}

        <div className="mb-6 rounded-[4px] border border-bordergray bg-white p-[22px] shadow-sm">
          <div className="mb-2.5 flex items-center gap-2.5">
            <CheckCircle size={18} weight="fill" color="#5b9a5e" />
            <span className="text-[20px] font-extrabold text-primary">
              {match?.legalName ?? ''}
            </span>
          </div>
          {/* The design also lists authority status and address here. The shipped lookup
              returns only `legal_name` + `identifier_type`, so this line stays with what we
              actually know rather than inventing registry detail. */}
          <div className="text-[14px] leading-relaxed text-gray-500">
            {matchedLabel}
          </div>
        </div>

        {renderPrimaryCta(
          HEADS_UP_COPY.confirmYesLabel,
          continueWithNumber,
          saving,
          saving
        )}
        {renderSecondaryLink(HEADS_UP_COPY.confirmNoLabel, () =>
          setPhase(HEADS_UP_PHASE_FORM)
        )}
      </>
    );
  };

  const renderConfirm = () => (
    <>
      {candidates.length > 1 ? renderAmbiguousConfirm() : renderSingleConfirm()}
    </>
  );

  /**
   * The two failure screens. They still differ in copy — and in whether the failure was counted
   * — but as of THT-738 neither offers a way past it: verification is REQUIRED, so re-running
   * the lookup is the only action on both.
   *
   * NOT FOUND — the registry answered and does not know this number. Fixing the number and
   * retrying is the way off, because saving a number the registry has already rejected just
   * moves the problem downstream. It counts toward the stuck alert.
   *
   * REGISTRY DOWN (500 / timeout / 503) — an unreachable registry is no evidence about the
   * number, so its copy asks for a retry in a few minutes instead of blaming the number, and it
   * deliberately does NOT count toward the stuck alert. The escape hatch that used to live here
   * is gone: John accepted the Highway dependency, and customer service now hears about anyone
   * who gets genuinely stuck rather than the flow quietly waving a bad number through.
   */
  const renderLookupFailed = () => {
    const notFound = failureReason === 'not_found';
    return (
      <>
        {renderHeader(
          notFound
            ? HEADS_UP_COPY.lookupNotFoundTitle
            : HEADS_UP_COPY.lookupFailedTitle,
          notFound ? (
            <>
              {HEADS_UP_COPY.lookupNotFoundBodyPrefix}
              {/* The number the registry was actually asked about — NOT the live field below,
                  which the customer is being invited to edit. */}
              <span className="font-semibold text-primary">
                {lookedUpNumber}
              </span>
              {HEADS_UP_COPY.lookupNotFoundBodySuffix}
            </>
          ) : (
            HEADS_UP_COPY.lookupFailedBody
          )
        )}

        <div className="mb-6 rounded-[4px] border border-bordergray bg-white px-6 py-5 shadow-sm">
          <label htmlFor="heads-up-retry-number" className={FIELD_LABEL}>
            {HEADS_UP_COPY.lookupFailedNumberLabel}
          </label>
          <input
            id="heads-up-retry-number"
            value={mcDotNumber}
            inputMode="numeric"
            maxLength={MC_DOT_MAX_LENGTH}
            placeholder={HEADS_UP_COPY.mcDotPlaceholder}
            onChange={(e) => handleNumberChange(e.target.value)}
            className={`${FIELD_INPUT} !border-[#d9a13c]`}
          />
          {renderNumberInvalidHint()}
          {/* The not-found body already tells the customer what to check, so the tip would only
              repeat it there; the registry-down screen, which says nothing about the number,
              keeps it. */}
          {!notFound && (
            <div className={`mt-2 text-[13px] ${HEADS_UP_META_TEXT_CLASS}`}>
              {HEADS_UP_COPY.lookupFailedTip}
            </div>
          )}
        </div>

        {/* Retrying the lookup is the ONLY action on both screens — most misses are a mistyped
            number or an MC/DOT mix-up, so correcting it above and re-running is what usually
            helps. It is guarded on a valid number, since the input above is editable. */}
        {renderPrimaryCta(
          HEADS_UP_COPY.tryAgainLabel,
          runLookup,
          !numberIsValid || saving
        )}

        {renderTeamNotified()}
      </>
    );
  };

  /**
   * A failed save is the one error on this page that costs the customer something, so it is
   * shown rather than swallowed: the button they just pressed retries, and the skip link keeps
   * the page from ever becoming a dead end (Business Info collects the same fields).
   */
  const renderSaveFailure = () => (
    <div className="mb-4 rounded-[4px] border border-[#e5d5b0] bg-[#fdf8ee] px-4 py-3 shadow-sm">
      <div className="text-[14px] font-semibold text-[#a07b2a]">
        {HEADS_UP_COPY.saveFailed}
      </div>
      <button
        type="button"
        className="mt-1 text-[13px] text-gray-500 underline"
        onClick={() => onContinue(false)}
      >
        {HEADS_UP_COPY.skipForNowLabel}
      </button>
    </div>
  );

  const renderPhase = () => {
    switch (phase) {
      case HEADS_UP_PHASE_INTRO:
        return renderIntro();
      case HEADS_UP_PHASE_LOOKING_UP:
        return renderLookingUp();
      case HEADS_UP_PHASE_CONFIRM:
        return renderConfirm();
      case HEADS_UP_PHASE_LOOKUP_FAILED:
        return renderLookupFailed();
      default:
        return renderForm();
    }
  };

  /**
   * The shell every customer signup step shares (see AccountInfo / BusinessInfo): a scrolling
   * page whose main column tops out at 800px, with the trust signals pinned alongside it on
   * desktop and stacked underneath on mobile. Every phase renders through it, so moving between
   * them never shifts the column or the sidebar.
   */
  return (
    <div className="flex h-full w-full flex-col items-center">
      <div className="w-full flex-1 overflow-y-auto py-10 lg:overflow-visible lg:py-20">
        <div className="flex w-full justify-center px-6 lg:px-12">
          <div className="mx-auto flex w-auto flex-col items-center justify-center gap-8 lg:flex-row lg:items-start">
            <div className="mx-auto flex-1 lg:w-[800px] lg:max-w-[800px]">
              {/* No wordmark here: signup.tsx mounts SignupBreadcrumbs for this page with a
                  non-step key, so the shared top bar already carries the TACH logo. */}
              {saveFailed && renderSaveFailure()}
              {renderPhase()}
            </div>

            {screenView === 'desktop' ? (
              <div className="sticky top-8 hidden h-fit w-[350px] lg:block">
                <TrustSignals variant="sidebar" />
              </div>
            ) : (
              <div className="mb-4 w-full max-w-[600px]">
                <TrustSignals variant="mobile" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeadsUp;
